import React, { useContext, useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import Box from '@mui/system/Box';
import { appContext, paths } from '@utils';
import { CustomButton } from '@components';
import { parsePhoneNumber } from 'react-phone-number-input';
import * as Yup from 'yup';
import { observer } from 'mobx-react';
import { Cover } from '../../components/Cover';
import { PhoneInputStep } from './steps/PhoneInputStep';
import { CodeVerificationStep } from './steps/CodeVerificationStep';
import { SuccessStep } from './steps/SuccessStep';
import { TwoFactorAuthSteps } from './steps.enum';

const makeStyles = () => ({
  pageContainer: {
    height: 'inherit',
    position: 'relative',
    backgroundColor: 'white',
    display: 'flex',
    overflow: 'hidden',
  },
  formContainer: {
    backgroundColor: 'white',
    width: '50%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    boxShadow: '-8px 0px 50px 0px rgba(5, 26, 97, 0.3)',
  },
  formContent: {
    margin: 'auto',
    width: '515px',
    display: 'flex',
    flexDirection: 'column',
    gap: '30px',
    alignItems: 'center',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    width: '276px',
  },
});

const phoneValidationSchema = Yup.object({
  phoneNumber: Yup.string().required('Phone number is required'),
});

const codeValidationSchema = Yup.object({
  code: Yup.string()
    .required('Verification code is required')
    .length(6, 'Code must be 6 digits')
    .matches(/^\d{6}$/, 'Code must contain only numbers'),
});

export const TwoFactorAuthFlow = observer(() => {
  const sx = makeStyles();
  const location = useLocation<any>();
  const history = useHistory();
  const {
    userStore: { twoFactorAuthState, verify2FACode, send2FACode, setSuperadminPhone },
  } = useContext(appContext);

  const [currentStep, setCurrentStep] = useState<TwoFactorAuthSteps>(
    TwoFactorAuthSteps.PHONE_INPUT,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect to login if no email/token in state
  useEffect(() => {
    if (!location.state?.email && !twoFactorAuthState.firebaseToken) {
      history.push(paths.login);
      return;
    }
    if (location.state?.step) {
      setCurrentStep(location.state.step);
    }
  }, [location.state, history, twoFactorAuthState.firebaseToken]);

  /*
  If the user is coming from the login page, we need to verify the device.
  */
  useEffect(() => {
    if (location.state?.step === TwoFactorAuthSteps.CODE_VERIFICATION) {
      send2FACode().then((success) => {
        if (success) {
          setCurrentStep(TwoFactorAuthSteps.CODE_VERIFICATION);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const phoneFormik = useFormik({
    initialValues: {
      phoneNumber: '',
      countryCode: 'CA',
    },
    validationSchema: phoneValidationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const parsed = parsePhoneNumber(values.phoneNumber);
        if (!parsed) return;
        const phone = parsed.nationalNumber;
        await setSuperadminPhone({ phone, countryCodeAlpha2: values.countryCode });
        setCurrentStep(TwoFactorAuthSteps.CODE_VERIFICATION);
      } catch (error) {
        // Error handled by store
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const codeFormik = useFormik({
    initialValues: {
      code: '',
    },
    validationSchema: codeValidationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        await verify2FACode(values.code);
        setCurrentStep(TwoFactorAuthSteps.SUCCESS);
      } catch (error) {
        // Error handled by store
        codeFormik.setFieldValue('code', '');
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleResendCode = async () => {
    setIsSubmitting(true);
    try {
      await send2FACode();
      codeFormik.setFieldValue('code', '');
    } catch (error) {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinue = () => {
    // Redirect to organizations page after successful 2FA
    history.push(paths.organizations);
  };

  return (
    <Box sx={sx.pageContainer}>
      <Cover />
      <Box sx={sx.formContainer}>
        <Box sx={sx.formContent}>
          {currentStep === TwoFactorAuthSteps.PHONE_INPUT && (
            <form
              onSubmit={phoneFormik.handleSubmit}
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '30px',
                alignItems: 'center',
              }}
            >
              <PhoneInputStep
                phoneNumber={phoneFormik.values.phoneNumber}
                onPhoneNumberChange={(value) => {
                  phoneFormik.setFieldValue('phoneNumber', value || '');
                }}
                countryCode={phoneFormik.values.countryCode}
                onCountryCodeChange={(country) => phoneFormik.setFieldValue('countryCode', country)}
              />
              <Box sx={sx.buttonContainer}>
                <CustomButton
                  variant="default"
                  type="submit"
                  isLoading={isSubmitting}
                  width="276px"
                >
                  Send Code
                </CustomButton>
              </Box>
            </form>
          )}

          {currentStep === TwoFactorAuthSteps.CODE_VERIFICATION && (
            <form
              onSubmit={codeFormik.handleSubmit}
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '48px',
                alignItems: 'center',
              }}
            >
              <CodeVerificationStep
                code={codeFormik.values.code}
                onCodeChange={(value) => codeFormik.setFieldValue('code', value)}
                maskedPhoneNumber={twoFactorAuthState.maskedPhoneNumber || '(***) ***-****'}
                maskedDestination={twoFactorAuthState.maskedPhoneNumber ? undefined : 'your email'}
                expiresAt={twoFactorAuthState.codeExpiresAt}
                returnedCode={twoFactorAuthState.returnedCode}
                onResend={handleResendCode}
                onVerify={() => codeFormik.handleSubmit()}
              />
              <Box sx={sx.buttonContainer}>
                <CustomButton
                  variant="default"
                  type="submit"
                  isLoading={isSubmitting}
                  width="276px"
                >
                  Verify Code
                </CustomButton>
              </Box>
            </form>
          )}

          {currentStep === TwoFactorAuthSteps.SUCCESS && (
            <>
              <SuccessStep />
              <Box sx={sx.buttonContainer}>
                <CustomButton variant="default" onClick={handleContinue} width="391px">
                  Continue
                </CustomButton>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
});
